export const SIGN_IN_REDIRECT_PARAM = 'redirect';

// only same-origin relative paths: an absolute or protocol-relative URL here would
// turn the sign-in page into an open redirect.
export const getSafeRedirectPath = (candidate: string | null) => {
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) {
    return null;
  }

  const resolved = new URL(candidate, window.location.origin);
  if (resolved.origin !== window.location.origin) {
    return null;
  }

  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
};

export const buildSignInPathWithRedirect = (signInPath: string, destination: string) =>
  `${signInPath}?${new URLSearchParams({ [SIGN_IN_REDIRECT_PARAM]: destination })}`;

const REMEMBERED_REDIRECT_KEY = 'signInRedirect';

// bounds how long an abandoned sign-in can send a later, unrelated login to its
// destination.
const REMEMBERED_REDIRECT_TTL_MS = 60 * 60 * 1000;

// magic link and SSO sign-ins return on a fresh page load without the redirect
// param, and a magic link often opens in another tab, so this has to be localStorage
// rather than sessionStorage. Storage throws in browsers that block it.
export const rememberSignInRedirect = (destination: string) => {
  try {
    localStorage.setItem(
      REMEMBERED_REDIRECT_KEY,
      JSON.stringify({ destination, expiresAt: Date.now() + REMEMBERED_REDIRECT_TTL_MS }),
    );
  } catch {
    // the sign-in still works, it just lands on the root
  }
};

export const clearRememberedSignInRedirect = () => {
  try {
    localStorage.removeItem(REMEMBERED_REDIRECT_KEY);
  } catch {
    // nothing to clear in a browser that blocks storage
  }
};

export const takeRememberedSignInRedirect = () => {
  try {
    const stored = localStorage.getItem(REMEMBERED_REDIRECT_KEY);
    localStorage.removeItem(REMEMBERED_REDIRECT_KEY);
    if (!stored) {
      return null;
    }

    const { destination, expiresAt } = JSON.parse(stored);
    if (typeof expiresAt !== 'number' || expiresAt < Date.now()) {
      return null;
    }

    return getSafeRedirectPath(typeof destination === 'string' ? destination : null);
  } catch {
    return null;
  }
};
